import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setAIAssistantDisplayed } from '@/aiAssistant/aiAssistant.actions';
import { getDefaultDisplayed } from '@/aiAssistant/aiAssistant.utilities';
import { subscribeToAuthChangeChannel } from '@/homescreen/homescreen.actions';
import { useLicenseManagementActions } from '@/licenseManagement/licenseManagement.store';
import { getLicenseStatusFromLicense, normalizeLicenseData } from '@/licenseManagement/licenseManagement.utilities';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { StoreProviders } from '@/main/StoreProviders';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import { setNavigate } from '@/main/routing.utilities';
import { isEmbeddedView } from '@/main/routing.utilities';
import { cancelRequestsOnUnload } from '@/requests/pendingRequests.utilities';
import { cleanupBroadcastChannels } from '@/services/broadcastChannel.utilities';
import { addEventListeners } from '@/services/notifier.service';
import { fetchConfiguration } from '@/services/systemConfiguration.utilities';
import { SystemMessageListener } from '@/systemWarning/SystemMessageListener';
import { Tracker } from '@/track/Tracker.atom';
import { isCsrfSet } from '@/utilities/auth.utilities';
import { init18n } from '@/utilities/i18n.utilities';
import { useApiGetLicenseQuery } from '@seeqdev/api/endpoint/SystemApi';

/**
 * Wrapper component that wraps all the Route components.
 * Everything that has to happen once per application load and applies to every single component should happen here.
 */
export const Main = () => {
  const navigate = useNavigate();
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const { setLicense, setLicenseStatus } = useLicenseManagementActions();
  const { data: license, isLoading: isLoadingLicense } = useApiGetLicenseQuery({ enabled: isCsrfSet() });

  useEffect(() => {
    if (!license) {
      return;
    }

    // Keep the license store in sync with the app-root license query.
    setLicense(normalizeLicenseData(license));
    setLicenseStatus(getLicenseStatusFromLicense(license));
  }, [license, setLicense, setLicenseStatus]);

  useEffect(() => {
    fetchConfiguration().then(() => {
      init18n()
        .then(() => {
          setIsI18nInitialized(true);
        })
        .catch((error) => {
          console.error('Error initializing i18n: ', error);
        });

      setConfigLoaded(true);
      if (!isEmbeddedView()) {
        setAIAssistantDisplayed(getDefaultDisplayed(), true);
      }
    });
    subscribeToAuthChangeChannel();
    addEventListeners();

    // we need to expose the navigate function to call it from services
    setNavigate(navigate);
    const beforeUnloadListener = () => cancelRequestsOnUnload();
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      cleanupBroadcastChannels();
      cancelRequestsOnUnload();
      cleanupOnUnload();
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, []);

  if (!configLoaded || !isI18nInitialized || isLoadingLicense) {
    return <LoadingFallback />;
  }

  return (
    <StoreProviders>
      <SystemMessageListener />
      <Tracker />
      <Outlet />
    </StoreProviders>
  );
};
